'use client';

import { useEffect, useRef } from 'react';
import { cn } from '@/app/utils';

interface ParticleSphereProps {
  /** 粒子数量。 */
  count?: number;
  /** 自转一圈的秒数，越大越慢。 */
  spinSeconds?: number;
  className?: string;
}

/** 黄金角：用于在球面上均匀铺点（Fibonacci sphere），避免极点堆积。 */
const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5));

/** 透视系数，越小透视越强（近大远小更明显）。 */
const PERSPECTIVE = 2.6;

/**
 * 中心粒子球：canvas 2D 画的球面点云，绕 Y 轴匀速自转。
 *
 * 用 Fibonacci 球面分布铺点，再做一次透视投影，靠 z 深度调节点的大小与透明度，
 * 形成「球在转」的体积感。不依赖 three.js —— 只有点云，2D canvas 足够且包体积为零。
 *
 * 性能与降级：
 * - devicePixelRatio 上限取 2，避免高分屏上画 3~4 倍像素
 * - IntersectionObserver 离屏时停画（首页很长，这一屏滚过去就不该继续耗 CPU）
 * - prefers-reduced-motion 时只静态画一帧，不自转
 */
export const ParticleSphere = ({ count = 900, spinSeconds = 42, className }: ParticleSphereProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // 球面均匀布点，顺带给少量点染上品牌蓝做点缀
    const points = Array.from({ length: count }, (_, i) => {
      const y = count > 1 ? 1 - (i / (count - 1)) * 2 : 0;
      const ring = Math.sqrt(Math.max(0, 1 - y * y));
      const theta = GOLDEN_ANGLE * i;
      return {
        x: Math.cos(theta) * ring,
        y,
        z: Math.sin(theta) * ring,
        tinted: i % 9 === 0,
      };
    });

    let width = 0;
    let height = 0;
    let radius = 0;
    let dpr = 1;

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = rect.width;
      height = rect.height;
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      radius = (Math.min(width, height) / 2) * 0.92;
    };

    let angle = 0;

    const draw = () => {
      if (!width || !height) return;
      ctx.clearRect(0, 0, width, height);
      const cx = width / 2;
      const cy = height / 2;
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);

      for (const p of points) {
        // 绕 Y 轴旋转
        const x = p.x * cos - p.z * sin;
        const z = p.x * sin + p.z * cos;
        const scale = PERSPECTIVE / (PERSPECTIVE - z);
        const depth = (z + 1) / 2; // 0=最远, 1=最近

        ctx.globalAlpha = 0.1 + depth * 0.7;
        ctx.fillStyle = p.tinted ? '#2E9CFF' : '#FFFFFF';
        ctx.beginPath();
        ctx.arc(cx + x * radius * scale, cy + p.y * radius * scale, 0.45 + depth * 1.15, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
    };

    resize();
    draw();

    const resizeObserver = new ResizeObserver(() => {
      resize();
      draw();
    });
    resizeObserver.observe(canvas);

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      return () => resizeObserver.disconnect();
    }

    let visible = true;
    const visibilityObserver = new IntersectionObserver(
      (entries) => {
        visible = entries[0]?.isIntersecting ?? true;
      },
      { rootMargin: '120px' },
    );
    visibilityObserver.observe(canvas);

    let raf = 0;
    let last = performance.now();
    const step = (Math.PI * 2) / spinSeconds;

    const tick = (now: number) => {
      const delta = Math.min((now - last) / 1000, 0.05);
      last = now;
      if (visible) {
        angle += step * delta;
        draw();
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      resizeObserver.disconnect();
      visibilityObserver.disconnect();
    };
  }, [count, spinSeconds]);

  return <canvas ref={canvasRef} aria-hidden className={cn('h-full w-full', className)} />;
};

export default ParticleSphere;
